import { useMemo } from "react";
import { Cpu, GitCommit, Star } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingState, ErrorState } from "@/components/ui/empty";
import { useApi } from "@/lib/api";
import { formatRelative, parseJson, shortHash } from "@/lib/format";
import type {
  Engine,
  EngineReposResponse,
  EnginesResponse,
} from "@/lib/types";

export function EnginesPage() {
  const repos = useApi<EngineReposResponse>("/api/engine_repos");
  const engines = useApi<EnginesResponse>("/api/engines");

  const byRepo = useMemo(() => {
    const m = new Map<string, Engine[]>();
    for (const e of engines.data?.engines ?? []) {
      const key = e.repo_id ?? "unknown";
      const list = m.get(key) ?? [];
      list.push(e);
      m.set(key, list);
    }
    return m;
  }, [engines.data]);

  if (repos.loading || engines.loading) return <LoadingState />;
  if (repos.error) return <ErrorState error={repos.error} />;
  if (engines.error) return <ErrorState error={engines.error} />;

  const repoList = repos.data?.engine_repos ?? [];

  return (
    <>
      <PageHeader
        title="Engines"
        description={`${engines.data?.count ?? 0} versions across ${repoList.length} engine repos.`}
      />

      <div className="space-y-8">
        {repoList.map((repo) => {
          const versions = byRepo.get(repo.id) ?? [];
          return (
            <section key={repo.id}>
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                        <Cpu className="h-3 w-3" />
                        <span>Engine repo</span>
                        <span>·</span>
                        <span className="font-mono">
                          prefix {repo.version_prefix}
                        </span>
                      </div>
                      <CardTitle className="mt-1 text-lg">
                        {repo.name}
                      </CardTitle>
                      <CardDescription className="mt-1 max-w-3xl text-xs">
                        {repo.description}
                      </CardDescription>
                    </div>
                    <Badge variant="secondary" className="font-mono">
                      {versions.length} version{versions.length === 1 ? "" : "s"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="relative pl-6">
                    <div className="absolute bottom-2 left-2 top-2 w-px bg-border" />
                    <ul className="space-y-3">
                      {versions.map((eng, i) => {
                        const layers = parseJson<number[]>(
                          eng.layers_supported,
                          [],
                        );
                        const isLatest = i === 0;
                        const canonical = eng.canonical === 1;
                        return (
                          <li key={eng.version} className="relative">
                            <div className="absolute -left-[18px] top-2 flex h-3 w-3 items-center justify-center">
                              <div
                                className={`h-2.5 w-2.5 rounded-full border-2 ${
                                  canonical
                                    ? "border-emerald-400 bg-emerald-500"
                                    : isLatest
                                      ? "border-indigo-400 bg-indigo-500"
                                      : "border-border bg-background"
                                }`}
                              />
                            </div>
                            <div className="flex items-start justify-between gap-4 rounded-md border bg-card px-3 py-2 text-xs">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-sm font-semibold">
                                    {eng.version}
                                  </span>
                                  {canonical && (
                                    <Badge variant="success" className="gap-1">
                                      <Star className="h-2.5 w-2.5" />
                                      canonical
                                    </Badge>
                                  )}
                                  {isLatest && !canonical && (
                                    <Badge
                                      variant="secondary"
                                      className="text-[10px]"
                                    >
                                      latest
                                    </Badge>
                                  )}
                                  {layers.length > 0 && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px]"
                                    >
                                      L
                                      {layers.join(",L")}
                                    </Badge>
                                  )}
                                </div>
                                <div className="mt-1 flex items-center gap-3 text-[10px] text-muted-foreground">
                                  <span className="inline-flex items-center gap-1 font-mono">
                                    <GitCommit className="h-3 w-3" />
                                    {shortHash(eng.git_hash)}
                                  </span>
                                  <span>
                                    registered {formatRelative(eng.created_at)}
                                  </span>
                                </div>
                                {eng.notes && (
                                  <div className="mt-1.5 line-clamp-2 text-[10px] text-muted-foreground">
                                    {eng.notes}
                                  </div>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                      {versions.length === 0 && (
                        <li className="text-xs text-muted-foreground">
                          No versions registered yet.
                        </li>
                      )}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </section>
          );
        })}
      </div>
    </>
  );
}
